import React, { createContext, useState, useEffect, useCallback } from 'react';
import i18n from 'i18next';
import { apiFetch } from '../utils/api';
import { capture } from '../analytics';

export const AuthContext = createContext(null);

const applyUserLanguage = (userData) => {
  const lang = userData && (userData.preferred_lang || userData.preferredLang);
  if (lang === 'ko' || lang === 'en') {
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem('i18nextLng', lang);
    } catch { /* i18n init race — ignore */ }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('veripass_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('veripass_token'));
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Validate stored token on mount, or check dev mode
  useEffect(() => {
    apiFetch('/api/v1/auth/me')
      .then(res => {
        if (!res.ok) throw new Error('Invalid session');
        return res.json();
      })
      .then(data => {
        const { permissions: perms, _token, ...userData } = data;
        setUser(userData);
        setPermissions(perms || {});
        localStorage.setItem('veripass_user', JSON.stringify(userData));
        applyUserLanguage(userData);
      })
      .catch(() => {
        if (token) {
          // Invalid token — clear stored auth
          setUser(null);
          setToken(null);
          setPermissions({});
          localStorage.removeItem('veripass_token');
          localStorage.removeItem('veripass_user');
        }
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password, replaceSessionId = null) => {
    // Collect basic device info for session tracking (no consent needed)
    const ua = navigator.userAgent;
    const deviceInfo = {
      browser: detectBrowser(ua),
      os: detectOS(ua),
      device_type: detectDeviceType(ua),
    };
    
    // Generate device_name
    deviceInfo.device_name = `${deviceInfo.browser || 'Browser'} on ${deviceInfo.os || 'Unknown'}`;
    
    const res = await apiFetch('/api/v1/auth/login', {
      method: 'POST',
      // A 401 here is "wrong email or password", not an expired session. Without
      // this, apiFetch swallows it and throws 'Session expired' before the
      // handling below ever runs.
      rawUnauthorized: true,
      // Logging in must not present the OLD token. It never needed to, and now
      // that a 401 here no longer clears storage, a stale or poisoned token sent
      // on this request would have no way to self-heal.
      skipAuth: true,
      body: JSON.stringify({ 
        email, 
        password,
        ...deviceInfo,
        ...(replaceSessionId ? { replace_session_id: replaceSessionId } : {}),
      }),
    });
    
    if (!res.ok) {
      // Check for session limit error (429)
      if (res.status === 429) {
        const errorData = await res.json().catch(() => ({}));
        // Throw with special type so caller can handle it
        const error = new Error('Session limit reached');
        error.type = 'session_limit';
        error.data = errorData.detail || errorData;
        throw error;
      }
      
      const err = await res.json().catch(() => ({}));
      const error = new Error(err.detail || 'Login failed');
      // The server's detail is English-only ("Invalid email or password"). A 401
      // on THIS request has exactly one meaning, so tag it and let the page render
      // the localised string — the default audience here is Korean, and until now
      // they saw neither that nor the English text, only "Session expired".
      if (res.status === 401) error.code = 'INVALID_CREDENTIALS';
      throw error;
    }
    
    const data = await res.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('veripass_token', data.token);
    localStorage.setItem('veripass_user', JSON.stringify(data.user));
    applyUserLanguage(data.user);

    // Fetch permissions
    try {
      const meRes = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/auth/me`,
        { headers: { 'Authorization': `Bearer ${data.token}` } }
      );
      if (meRes.ok) {
        const meData = await meRes.json();
        setPermissions(meData.permissions || {});
      }
    } catch { /* permissions will load on next mount */ }
    return data;
  }, []);

  const register = useCallback(async (email, password, fullName, orgName, deviceData = null, preferredLang = null) => {
    const payload = { 
      email, 
      password, 
      full_name: fullName, 
      org_name: orgName,
      preferred_lang: preferredLang || (
        (typeof navigator !== 'undefined' && navigator.language && navigator.language.startsWith('ko')) ? 'ko' : 'en'
      ),
    };
    
    // Add device tracking data if provided
    if (deviceData) {
      payload.device_fingerprint = deviceData.deviceFingerprint;
      payload.user_agent = deviceData.userAgent;
      payload.browser = deviceData.browser;
      payload.os = deviceData.os;
      payload.device_type = deviceData.deviceType;
      payload.timezone = deviceData.timezone;
      payload.tracking_consent = true;
    }
    
    const res = await apiFetch('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = body.detail;
      // G1: the backend now returns a STRUCTURED detail for an existing but
      // UNVERIFIED account ({error, message, verification_email_sent, email}).
      // Surface those fields so the caller can route the user to the pending
      // screen instead of dead-ending them on "Email already registered".
      // (A plain-string detail keeps the old behaviour.)
      const structured = detail && typeof detail === 'object';
      const err = new Error(
        (structured ? detail.message : detail) || 'Registration failed'
      );
      err.status = res.status;
      if (structured) {
        err.code = detail.error;
        err.email = detail.email;
        err.verificationEmailSent = detail.verification_email_sent;
      }
      throw err;
    }
    const data = await res.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('veripass_token', data.token);
    localStorage.setItem('veripass_user', JSON.stringify(data.user));
    applyUserLanguage(data.user);

    // Signup conversion event (single choke point for all signup entries). No PII.
    capture('signup');

    // Fetch permissions immediately so protected routes work
    try {
      const meRes = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/auth/me`,
        { headers: { 'Authorization': `Bearer ${data.token}` } }
      );
      if (meRes.ok) {
        const meData = await meRes.json();
        setPermissions(meData.permissions || {});
      }
    } catch { /* permissions will load on next mount */ }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/v1/auth/logout', { method: 'POST' });
    } catch { /* ignore */ }
    setUser(null);
    setToken(null);
    setPermissions({});
    localStorage.removeItem('veripass_token');
    localStorage.removeItem('veripass_user');
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await apiFetch('/api/v1/auth/me');
    if (!res.ok) return null;
    const data = await res.json();
    const { permissions: perms, _token, ...userData } = data;
    setUser(userData);
    if (perms) setPermissions(perms);
    localStorage.setItem('veripass_user', JSON.stringify(userData));
    applyUserLanguage(userData);
    return userData;
  }, []);

  const updatePreferredLang = useCallback(async (lang) => {
    if (lang !== 'ko' && lang !== 'en') return null;
    const res = await apiFetch('/api/v1/profile', {
      method: 'PUT',
      body: JSON.stringify({ preferred_lang: lang }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to save language preference');
    }
    const updatedUser = await res.json();
    setUser(prev => ({ ...(prev || {}), ...updatedUser, preferred_lang: lang }));
    localStorage.setItem('veripass_user', JSON.stringify({ ...(user || {}), ...updatedUser, preferred_lang: lang }));
    applyUserLanguage({ preferred_lang: lang });
    return updatedUser;
  }, [user]);

  const sendVerifyCode = useCallback(async (email) => {
    const res = await apiFetch('/api/v1/auth/verify/send', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = body.detail || body;
      const err = new Error(detail.message || detail.error || 'Failed to send code');
      err.status = res.status;
      err.retryAfter = detail.retry_after_s;
      throw err;
    }
    return body;
  }, []);

  const resendVerifyCode = useCallback(async (email) => {
    const res = await apiFetch('/api/v1/auth/verify/resend', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = body.detail || body;
      const err = new Error(detail.message || detail.error || 'Failed to resend code');
      err.status = res.status;
      err.retryAfter = detail.retry_after_s;
      throw err;
    }
    return body;
  }, []);

  const verifyCode = useCallback(async (email, code) => {
    const res = await apiFetch('/api/v1/auth/verify/confirm', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
      skipAuth: true,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = body.detail || body;
      const err = new Error(detail.message || detail.error || 'Verification failed');
      err.status = res.status;
      err.code = detail.error;
      err.attemptsRemaining = detail.attempts_remaining;
      throw err;
    }
    // Mark user as verified locally
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, email_verified_at: body.verified_at || new Date().toISOString() };
      localStorage.setItem('veripass_user', JSON.stringify(updated));
      return updated;
    });
    return body;
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    const p = permissions[perm];
    if (p) return p.granted;
    return false;
  }, [user, permissions]);

  const isAuthenticated = !!user && !!token;
  const isSiteAdmin = !!(user?.is_root || user?.can_manage_admins);

  return (
    <AuthContext.Provider value={{
      user, token, permissions, loading,
      isAuthenticated, isSiteAdmin, login, register, logout, hasPermission,
      updatePreferredLang, refreshUser,
      sendVerifyCode, resendVerifyCode, verifyCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Helper functions for device detection (no consent required)
function detectBrowser(ua) {
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Chrome';
  if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Opera/') || ua.includes('OPR/')) return 'Opera';
  return 'Browser';
}

function detectOS(ua) {
  if (ua.includes('Windows NT 10.0')) return 'Windows 10';
  if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
}

function detectDeviceType(ua) {
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
    return 'Mobile';
  }
  if (ua.includes('Tablet') || ua.includes('iPad')) {
    return 'Tablet';
  }
  return 'Desktop';
}
